import React, { useState, useEffect } from 'react';
import { useSetCurrentPage } from '../stores/useAppStore';
import GradientBackground from '../components/ui/GradientBackground';
import ParticleBackground from '../components/ui/ParticleBackground';
import GlassCard from '../components/ui/GlassCard';
import AnimatedTitle from '../components/ui/AnimatedTitle';
import ScrollReveal from '../components/ui/ScrollReveal';
import PageHeader from '../components/PageHeader';
import {
  stockStrategyService,
  OptimizedResults,
  TelegramAlertConfig,
  PortfolioAllocationResult,
  PortfolioBacktestResult,
  WFOResult,
} from '../services/stockStrategyService';
import { StockMonitorTask } from '../services/stockMonitorService';
import { stockService, StockQuantPosition, QuantPortfolioSummary } from '../services/stockService';
import { VirtualPositionsTable } from '../components/Stock/Intraday/VirtualPositionsTable';
import { VirtualPortfolioSummary } from '../components/Stock/Intraday/VirtualPortfolioSummary';
import ReactMarkdown from 'react-markdown';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  ReferenceLine,
} from 'recharts';

const MCPathChart: React.FC<{ paths: number[][] }> = ({ paths }) => {
  if (!paths || paths.length === 0) return null;

  const chartData = paths[0].map((_, timeIdx) => {
    const point: any = { time: timeIdx };
    paths.forEach((path, pathIdx) => {
      point[`p${pathIdx}`] = (path[timeIdx] * 100).toFixed(2);
    });
    return point;
  });

  return (
    <div className="h-[200px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#ffffff05" vertical={false} />
          <XAxis hide dataKey="time" />
          <YAxis
            stroke="#ffffff20"
            fontSize={10}
            tickLine={false}
            axisLine={false}
            tickFormatter={(val) => `${val}%`}
            domain={['auto', 'auto']}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: '#0f172a',
              border: '1px solid #ffffff10',
              borderRadius: '12px',
              fontSize: '10px',
            }}
            labelStyle={{ display: 'none' }}
          />
          {paths.map((_, idx) => (
            <Line
              key={idx}
              type="monotone"
              dataKey={`p${idx}`}
              stroke={idx === 0 ? '#3b82f6' : '#ffffff10'}
              strokeWidth={idx === 0 ? 2 : 1}
              dot={false}
              isAnimationActive={false}
            />
          ))}
          <ReferenceLine y={100} stroke="#ffffff20" strokeDasharray="3 3" />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

const SensitivityHeatmap: React.FC<{ data: any[] }> = ({ data }) => {
  if (!data || data.length === 0) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {data.map((param) => (
        <div key={param.name} className="bg-white/5 rounded-xl p-3 border border-white/5">
          <div className="flex justify-between items-center mb-3">
            <span className="text-[10px] font-bold text-white/60">{param.name}</span>
            <span className={`text-[10px] ${param.stability > 0.7 ? 'text-green-400' : 'text-orange-400'}`}>
              稳定性: {(param.stability * 100).toFixed(0)}%
            </span>
          </div>
          <div className="flex gap-1 h-8">
            {param.variations.map((v: any, idx: number) => {
              const impactColor =
                v.impact > 0.05 ? 'bg-green-500/40' : v.impact < -0.05 ? 'bg-red-500/40' : 'bg-blue-500/40';
              return (
                <div key={idx} className={`flex-1 ${impactColor} rounded-sm cursor-help relative group`}>
                  <div className="hidden group-hover:block absolute bottom-full left-1/2 -translate-x-1/2 mb-2 p-2 bg-slate-900 border border-white/10 rounded text-[10px] text-white whitespace-nowrap z-50">
                    值: {v.value} | 影响: {(v.impact * 100).toFixed(1)}%
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

const AlertConfigModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  config: TelegramAlertConfig;
  onUpdate: (config: Partial<TelegramAlertConfig>) => Promise<void>;
}> = ({ isOpen, onClose, config, onUpdate }) => {
  const [localConfig, setLocalConfig] = useState(config);
  const [saving, setSaving] = useState(false);

  if (!isOpen) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdate(localConfig);
      onClose();
    } catch (err) {
      alert('保存失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
      <GlassCard className="w-full max-w-md p-6 border-white/20">
        <AnimatedTitle variant="glow" size="sm" className="mb-6">
          <span>✈️</span> Telegram 告警设置
        </AnimatedTitle>

        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-white font-medium">启用告警</div>
              <div className="text-white/40 text-xs">开启后将发送实时信号到 Telegram</div>
            </div>
            <button
              onClick={() => setLocalConfig({ ...localConfig, isEnabled: !localConfig.isEnabled })}
              className={`w-12 h-6 rounded-full transition-colors relative ${localConfig.isEnabled ? 'bg-blue-500' : 'bg-white/10'}`}
            >
              <div
                className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${localConfig.isEnabled ? 'left-7' : 'left-1'}`}
              />
            </button>
          </div>

          <div>
            <div className="text-white font-medium mb-2">Chat ID / 频道 ID</div>
            <input
              type="text"
              value={localConfig.chatId || ''}
              onChange={(e) => setLocalConfig({ ...localConfig, chatId: e.target.value })}
              placeholder="例如: -100123456789"
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-white text-sm focus:outline-none focus:border-blue-500/50"
            />
          </div>

          <div>
            <div className="text-white font-medium mb-2">发送频率</div>
            <div className="grid grid-cols-1 gap-2">
              {[
                { id: 'every_signal', label: '即时发送 (每条信号)' },
                { id: 'hourly_summary', label: '每小时汇总' },
                { id: 'daily_summary', label: '每日汇总' },
              ].map((freq) => (
                <button
                  key={freq.id}
                  onClick={() => setLocalConfig({ ...localConfig, alertFrequency: freq.id as any })}
                  className={`px-4 py-2 rounded-xl text-sm border transition-all text-left ${
                    localConfig.alertFrequency === freq.id
                      ? 'bg-blue-500/20 border-blue-500/50 text-blue-400'
                      : 'bg-white/5 border-white/10 text-white/50 hover:bg-white/10'
                  }`}
                >
                  {freq.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex-1">
              <div className="text-white font-medium mb-2">买入信号</div>
              <button
                onClick={() => setLocalConfig({ ...localConfig, enableBuySignals: !localConfig.enableBuySignals })}
                className={`w-full py-2 rounded-xl text-sm border transition-all ${
                  localConfig.enableBuySignals
                    ? 'bg-green-500/20 border-green-500/50 text-green-400'
                    : 'bg-white/5 border-white/10 text-white/50'
                }`}
              >
                {localConfig.enableBuySignals ? '已开启' : '已关闭'}
              </button>
            </div>
            <div className="flex-1">
              <div className="text-white font-medium mb-2">卖出信号</div>
              <button
                onClick={() => setLocalConfig({ ...localConfig, enableSellSignals: !localConfig.enableSellSignals })}
                className={`w-full py-2 rounded-xl text-sm border transition-all ${
                  localConfig.enableSellSignals
                    ? 'bg-red-500/20 border-red-500/50 text-red-400'
                    : 'bg-white/5 border-white/10 text-white/50'
                }`}
              >
                {localConfig.enableSellSignals ? '已开启' : '已关闭'}
              </button>
            </div>
          </div>
        </div>

        <div className="mt-8 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-3 bg-white/5 hover:bg-white/10 rounded-xl text-white font-medium transition-all"
          >
            取消
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 py-3 bg-blue-500 hover:bg-blue-600 rounded-xl text-white font-medium transition-all shadow-lg shadow-blue-500/25 disabled:opacity-50"
          >
            {saving ? '保存中...' : '保存设置'}
          </button>
        </div>
      </GlassCard>
    </div>
  );
};

const AllocationModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onFetch: (capital: number) => Promise<PortfolioAllocationResult>;
}> = ({ isOpen, onClose, onFetch }) => {
  const [capital, setCapital] = useState(1000000);
  const [result, setResult] = useState<PortfolioAllocationResult | null>(null);
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleFetch = async () => {
    setLoading(true);
    try {
      const data = await onFetch(capital);
      setResult(data);
    } catch (err) {
      alert('获取建议失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
      <GlassCard className="w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6 border-white/20">
        <div className="flex justify-between items-center mb-6">
          <AnimatedTitle variant="glow" size="sm" className="mb-0">
            <span>💰</span> 组合资金分配建议 (风险等权)
          </AnimatedTitle>
          <button onClick={onClose} className="text-white/40 hover:text-white">
            ✕
          </button>
        </div>

        <div className="flex gap-4 mb-8">
          <div className="flex-1">
            <div className="text-white/60 text-xs mb-1">总投入资金 (CNY)</div>
            <input
              type="number"
              value={capital}
              onChange={(e) => setCapital(Number(e.target.value))}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-white focus:outline-none focus:border-blue-500/50"
            />
          </div>
          <button
            onClick={handleFetch}
            disabled={loading}
            className="self-end px-8 py-2 bg-blue-500 hover:bg-blue-600 rounded-xl text-white font-medium transition-all disabled:opacity-50"
          >
            {loading ? '计算中...' : '生成建议'}
          </button>
        </div>

        {result && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <GlassCard className="p-4 bg-white/5">
                <div className="text-white/40 text-xs">标的数量</div>
                <div className="text-xl font-bold text-white">{result.allocations.length}</div>
              </GlassCard>
              <GlassCard className="p-4 bg-white/5">
                <div className="text-white/40 text-xs">组合风险评分 (波动率)</div>
                <div className="text-xl font-bold text-blue-400">{(result.totalRiskScore * 100).toFixed(4)}%</div>
              </GlassCard>
              <GlassCard className="p-4 bg-white/5">
                <div className="text-white/40 text-xs">资金利用率</div>
                <div className="text-xl font-bold text-green-400">100%</div>
              </GlassCard>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="text-white/40 text-xs uppercase tracking-wider border-b border-white/10">
                    <th className="px-4 py-3">标的</th>
                    <th className="px-4 py-3">波动率 (20D)</th>
                    <th className="px-4 py-3">分配权重</th>
                    <th className="px-4 py-3">建议金额</th>
                    <th className="px-4 py-3">预期年化</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {result.allocations.map((item) => (
                    <tr key={item.symbol} className="hover:bg-white/5 transition-colors">
                      <td className="px-4 py-4">
                        <div className="text-white font-medium">{item.name}</div>
                        <div className="text-white/40 text-xs">{item.symbol}</div>
                      </td>
                      <td className="px-4 py-4 text-white/80">{(item.volatility * 100).toFixed(4)}%</td>
                      <td className="px-4 py-4">
                        <div className="w-full bg-white/5 h-1.5 rounded-full mb-1">
                          <div className="bg-blue-500 h-full rounded-full" style={{ width: `${item.weight * 100}%` }} />
                        </div>
                        <div className="text-blue-400 text-xs font-medium">{(item.weight * 100).toFixed(2)}%</div>
                      </td>
                      <td className="px-4 py-4 text-green-400 font-bold">
                        ¥{item.suggestedCapital.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </td>
                      <td className="px-4 py-4 text-orange-400">{(item.expectedAnnualReturn * 100).toFixed(2)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="text-white/30 text-[10px] italic">
              注：波动率基于过去20个交易日的日收益率标准差计算。权重分配采用风险等权 (Risk Parity)
              模型，波动率越高分配金额越少。
            </div>
          </div>
        )}
      </GlassCard>
    </div>
  );
};

const WFOAnalysisSection: React.FC = () => {
  const [result, setResult] = useState<WFOResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [wfoConfig, setWfoConfig] = useState({
    symbol: 'sz002131',
    strategyTemplate: 'trend_follow',
    trainMonths: 4,
    testMonths: 2,
    stepMonths: 2,
  });

  const fetchLatestResult = async () => {
    setLoading(true);
    try {
      const data = await stockStrategyService.getLatestWFOResult();
      setResult(data);
    } catch (err) {
      console.error('Failed to fetch WFO result:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRunWFO = async () => {
    setRunning(true);
    try {
      const config = {
        symbol: wfoConfig.symbol,
        strategyTemplate: wfoConfig.strategyTemplate,
        totalStart: new Date('2024-01-01'),
        totalEnd: new Date('2024-12-31'),
        trainMonths: wfoConfig.trainMonths,
        testMonths: wfoConfig.testMonths,
        stepMonths: wfoConfig.stepMonths,
        initialCapital: 1000000,
        searchIterations: 10,
      };
      await stockStrategyService.runWFOAnalysis(config);
      await fetchLatestResult();
    } catch (err) {
      console.error(err);
      alert('运行 WFO 失败');
    } finally {
      setRunning(false);
    }
  };

  useEffect(() => {
    fetchLatestResult();
  }, []);

  if (!result && !loading) {
    return (
      <GlassCard className="p-4 mb-4 border-white/10 flex flex-col items-center justify-center text-center">
        <div className="text-4xl mb-4">🔄</div>
        <AnimatedTitle variant="glow" size="sm" className="mb-2">
          步进式寻优 (WFO) 分析
        </AnimatedTitle>
        <p className="text-white/40 text-sm max-w-md mb-6">
          通过滚动窗口模拟“训练-测试”过程，验证策略在未见数据上的表现，计算 WFE 指标与动态风险乘数。
        </p>

        {/* 简易配置区 */}
        <div className="grid grid-cols-2 gap-4 w-full max-w-md mb-6">
          <div>
            <label className="text-xs text-white/40 block mb-1">训练窗口 (月)</label>
            <input
              type="number"
              value={wfoConfig.trainMonths}
              onChange={(e) => setWfoConfig({ ...wfoConfig, trainMonths: Number(e.target.value) })}
              className="w-full bg-white/5 border border-white/10 rounded px-2 py-1 text-white text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-white/40 block mb-1">测试窗口 (月)</label>
            <input
              type="number"
              value={wfoConfig.testMonths}
              onChange={(e) => setWfoConfig({ ...wfoConfig, testMonths: Number(e.target.value) })}
              className="w-full bg-white/5 border border-white/10 rounded px-2 py-1 text-white text-sm"
            />
          </div>
        </div>

        <button
          onClick={handleRunWFO}
          disabled={running}
          className="px-8 py-3 bg-indigo-500 hover:bg-indigo-600 rounded-xl text-white font-medium transition-all shadow-lg shadow-indigo-500/25 disabled:opacity-50"
        >
          {running ? '正在进行步进优化...' : '开始 WFO 稳健性分析'}
        </button>
      </GlassCard>
    );
  }

  const chartData =
    result?.windows.map((w) => ({
      name: `W${w.window.id}`,
      isReturn: (w.trainMetrics.totalReturnPct || 0).toFixed(2),
      oosReturn: (w.testMetrics.totalReturnPct || 0).toFixed(2),
      multiplier: w.riskMultiplier.toFixed(2),
    })) || [];

  return (
    <ScrollReveal animation="fadeInUp">
      <GlassCard className="p-4 mb-4 border-white/10">
        <div className="flex justify-between items-center mb-6">
          <div>
            <AnimatedTitle variant="glow" size="sm" className="mb-0">
              Walk-Forward Optimization (WFO)
            </AnimatedTitle>
            <p className="text-white/40 text-[10px] mt-1">
              策略: {result?.windows[0]?.bestParams?.strategyTemplate || wfoConfig.strategyTemplate} | 稳定性评分:{' '}
              {(result?.sustainabilityScore || 0).toFixed(2)}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowConfig(!showConfig)}
              className="px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-white text-xs transition-all flex items-center gap-1.5"
            >
              <span>⚙️</span> 参数设置
            </button>
            <button
              onClick={handleRunWFO}
              disabled={running}
              className="px-3 py-1.5 bg-indigo-500/20 hover:bg-indigo-500/30 border border-indigo-500/50 rounded-xl text-indigo-400 text-xs transition-all flex items-center gap-1.5"
            >
              {running ? (
                <div className="w-3 h-3 border-2 border-indigo-400/30 border-t-indigo-400 rounded-full animate-spin" />
              ) : (
                <span>🔄</span>
              )}
              {running ? '运行中...' : '重新运行'}
            </button>
          </div>
        </div>

        {showConfig && (
          <div className="mb-6 p-4 bg-white/5 rounded-xl border border-white/5 grid grid-cols-2 md:grid-cols-4 gap-4 animate-in slide-in-from-top-2">
            <div>
              <label className="text-xs text-white/40 block mb-1">标的代码</label>
              <input
                type="text"
                value={wfoConfig.symbol}
                onChange={(e) => setWfoConfig({ ...wfoConfig, symbol: e.target.value })}
                className="w-full bg-black/20 border border-white/10 rounded px-2 py-1 text-white text-xs"
              />
            </div>
            <div>
              <label className="text-xs text-white/40 block mb-1">策略模板</label>
              <select
                value={wfoConfig.strategyTemplate}
                onChange={(e) => setWfoConfig({ ...wfoConfig, strategyTemplate: e.target.value })}
                className="w-full bg-black/20 border border-white/10 rounded px-2 py-1 text-white text-xs"
              >
                <option value="trend_follow">趋势跟踪 (Trend)</option>
                <option value="vol_breakout">波动突破 (Vol)</option>
                <option value="rsi_oversold">RSI 均值回归</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-white/40 block mb-1">训练窗口 (月)</label>
              <input
                type="number"
                value={wfoConfig.trainMonths}
                onChange={(e) => setWfoConfig({ ...wfoConfig, trainMonths: Number(e.target.value) })}
                className="w-full bg-black/20 border border-white/10 rounded px-2 py-1 text-white text-xs"
              />
            </div>
            <div>
              <label className="text-xs text-white/40 block mb-1">测试窗口 (月)</label>
              <input
                type="number"
                value={wfoConfig.testMonths}
                onChange={(e) => setWfoConfig({ ...wfoConfig, testMonths: Number(e.target.value) })}
                className="w-full bg-black/20 border border-white/10 rounded px-2 py-1 text-white text-xs"
              />
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          <div className="p-4 bg-white/5 rounded-2xl border border-white/5">
            <div className="text-white/40 text-[10px] uppercase tracking-wider mb-1">IS/OOS 效率 (WFE)</div>
            <div
              className={`text-xl font-bold ${result?.aggregatedOOSMetrics?.wfe && result.aggregatedOOSMetrics.wfe > 0.5 ? 'text-green-400' : 'text-orange-400'}`}
            >
              {(result?.aggregatedOOSMetrics?.wfe || 0).toFixed(2)}
            </div>
            <div className="text-[10px] text-white/20 mt-1">越接近 1.0 越稳健</div>
          </div>

          <div className="p-4 bg-white/5 rounded-2xl border border-white/5 relative group">
            <div className="text-white/40 text-[10px] uppercase tracking-wider mb-1 flex items-center gap-1">
              PSR (概率夏普) <span className="text-white/20 cursor-help">?</span>
            </div>
            <div
              className={`text-xl font-bold ${result?.psr && result.psr > 0.95 ? 'text-green-400' : result?.psr && result.psr > 0.9 ? 'text-blue-400' : 'text-orange-400'}`}
            >
              {(result?.psr || 0).toFixed(2)}
            </div>
            <div className="text-[10px] text-white/20 mt-1">
              {result?.psr && result.psr > 0.95 ? '极高置信度' : '需谨慎验证'}
            </div>

            <div className="hidden group-hover:block absolute bottom-full left-0 mb-2 p-2 bg-slate-900 border border-white/10 rounded text-[10px] text-white w-48 z-50">
              Probabilistic Sharpe Ratio: 评估夏普比率非偶然获得的概率。PSR {'>'} 0.95 表示策略具有统计显著性。
            </div>
          </div>

          <div className="p-4 bg-white/5 rounded-2xl border border-white/5">
            <div className="text-white/40 text-[10px] uppercase tracking-wider mb-1">平均风险乘数</div>
            <div className="text-xl font-bold text-blue-400">
              {(
                (result?.windows || []).reduce((acc, w) => acc + w.riskMultiplier, 0) / (result?.windows?.length || 1)
              ).toFixed(2)}
              x
            </div>
            <div className="text-[10px] text-white/20 mt-1">基于稳定性自动调节</div>
          </div>
          <div className="p-4 bg-white/5 rounded-2xl border border-white/5">
            <div className="text-white/40 text-[10px] uppercase tracking-wider mb-1">样本外总收益</div>
            <div
              className={`text-xl font-bold ${result?.aggregatedOOSMetrics?.totalReturnPct && result.aggregatedOOSMetrics.totalReturnPct > 0 ? 'text-green-400' : 'text-red-400'}`}
            >
              {(result?.aggregatedOOSMetrics?.totalReturnPct || 0).toFixed(2)}%
            </div>
          </div>
          <div className="p-4 bg-white/5 rounded-2xl border border-white/5">
            <div className="text-white/40 text-[10px] uppercase tracking-wider mb-1">稳定性等级</div>
            <div className="text-xl font-bold text-indigo-400">
              {(result?.sustainabilityScore || 0) > 0.7
                ? '极佳'
                : (result?.sustainabilityScore || 0) > 0.4
                  ? '良好'
                  : '风险'}
            </div>
          </div>
        </div>

        <div className="h-[250px] w-full mb-8">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff05" vertical={false} />
              <XAxis dataKey="name" stroke="#ffffff20" fontSize={10} tickLine={false} axisLine={false} />
              <YAxis stroke="#ffffff20" fontSize={10} tickLine={false} axisLine={false} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#0f172a',
                  border: '1px solid #ffffff10',
                  borderRadius: '12px',
                  fontSize: '12px',
                }}
                itemStyle={{ color: '#fff' }}
              />
              <Bar dataKey="isReturn" name="样本内 (IS)" fill="#3b82f640" radius={[4, 4, 0, 0]} />
              <Bar dataKey="oosReturn" name="样本外 (OOS)" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* 深度科学验证可视化 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* 蒙特卡洛模拟路径 */}
          <div className="bg-white/5 rounded-2xl p-5 border border-white/5">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-sm font-bold text-white/80 flex items-center gap-2">
                <span>🎲</span> 蒙特卡洛模拟路径 (压力测试)
              </h4>
              <div className="text-[10px] text-white/30">1000次抽样重组</div>
            </div>
            {result?.latestMonteCarlo?.paths ? (
              <MCPathChart paths={result.latestMonteCarlo.paths} />
            ) : (
              <div className="h-[200px] flex items-center justify-center text-white/20 text-xs italic">
                暂无模拟数据
              </div>
            )}
            <div className="mt-4 grid grid-cols-2 gap-2">
              <div className="p-2 bg-white/5 rounded-lg">
                <div className="text-[9px] text-white/40 uppercase">失败概率</div>
                <div className="text-xs font-bold text-red-400">
                  {((result?.latestMonteCarlo?.failureProbability || 0) * 100).toFixed(1)}%
                </div>
              </div>
              <div className="p-2 bg-white/5 rounded-lg">
                <div className="text-[9px] text-white/40 uppercase">破产风险 (30%回撤)</div>
                <div className="text-xs font-bold text-orange-400">
                  {((result?.latestMonteCarlo?.riskOfRuin || 0) * 100).toFixed(1)}%
                </div>
              </div>
            </div>
          </div>

          {/* 参数敏感度热力图 */}
          <div className="bg-white/5 rounded-2xl p-5 border border-white/5">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-sm font-bold text-white/80 flex items-center gap-2">
                <span>🧪</span> 参数敏感度分析 (稳健性)
              </h4>
              <div className="text-[10px] text-white/30">±20% 扰动测试</div>
            </div>
            {result?.latestSensitivity?.parameterSensitivity ? (
              <SensitivityHeatmap data={result.latestSensitivity.parameterSensitivity} />
            ) : (
              <div className="h-[200px] flex items-center justify-center text-white/20 text-xs italic">
                暂无敏感度数据
              </div>
            )}
            <p className="mt-4 text-[9px] text-white/40 leading-relaxed">
              蓝色方块代表基准参数，绿色代表性能提升，红色代表下降。理想情况下应呈现连续的蓝色/绿色区域。
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <h4 className="text-sm font-bold text-white/80">窗口明细</h4>
          <div className="grid grid-cols-1 gap-3">
            {result?.windows.map((w, idx) => (
              <div key={idx} className="bg-white/5 rounded-xl p-4 border border-white/5 flex flex-col gap-3">
                <div className="flex flex-wrap justify-between items-center gap-4">
                  <div className="flex-1 min-w-[200px]">
                    <div className="text-xs font-bold text-white">
                      窗口 {w.window.id} ({new Date(w.window.testStart).toLocaleDateString()} ~{' '}
                      {new Date(w.window.testEnd).toLocaleDateString()})
                    </div>
                    <div className="text-[10px] text-white/40 mt-1">最佳参数: {JSON.stringify(w.bestParams)}</div>
                  </div>
                  <div className="flex gap-6">
                    <div className="text-center">
                      <div className="text-[10px] text-white/40 uppercase">IS 收益</div>
                      <div className="text-sm font-bold text-blue-400">{w.trainMetrics.totalReturnPct.toFixed(2)}%</div>
                    </div>
                    <div className="text-center">
                      <div className="text-[10px] text-white/40 uppercase">OOS 收益</div>
                      <div className="text-sm font-bold text-green-400">{w.testMetrics.totalReturnPct.toFixed(2)}%</div>
                    </div>
                    <div className="text-center">
                      <div className="text-[10px] text-white/40 uppercase">风险系数</div>
                      <div className="text-sm font-bold text-indigo-400">{w.riskMultiplier.toFixed(2)}x</div>
                    </div>
                  </div>
                </div>

                {w.brinsonAttribution && (
                  <div className="w-full pt-3 border-t border-white/5 grid grid-cols-4 gap-2 text-center bg-white/5 rounded-lg p-2">
                    <div>
                      <div className="text-[9px] text-white/30 uppercase">配置效应 (Allocation)</div>
                      <div
                        className={`text-xs font-bold ${w.brinsonAttribution.allocationEffect > 0 ? 'text-green-400' : 'text-red-400'}`}
                      >
                        {(w.brinsonAttribution.allocationEffect * 100).toFixed(2)}%
                      </div>
                    </div>
                    <div>
                      <div className="text-[9px] text-white/30 uppercase">选股效应 (Selection)</div>
                      <div
                        className={`text-xs font-bold ${w.brinsonAttribution.selectionEffect > 0 ? 'text-green-400' : 'text-red-400'}`}
                      >
                        {(w.brinsonAttribution.selectionEffect * 100).toFixed(2)}%
                      </div>
                    </div>
                    <div>
                      <div className="text-[9px] text-white/30 uppercase">交互效应 (Interaction)</div>
                      <div
                        className={`text-xs font-bold ${w.brinsonAttribution.interactionEffect > 0 ? 'text-green-400' : 'text-red-400'}`}
                      >
                        {(w.brinsonAttribution.interactionEffect * 100).toFixed(2)}%
                      </div>
                    </div>
                    <div>
                      <div className="text-[9px] text-white/30 uppercase">总超额 (Excess)</div>
                      <div
                        className={`text-xs font-bold ${w.brinsonAttribution.totalExcessReturn > 0 ? 'text-green-400' : 'text-red-400'}`}
                      >
                        {(w.brinsonAttribution.totalExcessReturn * 100).toFixed(2)}%
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 p-4 bg-indigo-500/5 border border-indigo-500/10 rounded-xl">
          <div className="text-indigo-400 text-xs font-bold mb-1 flex items-center gap-1">
            <span>🧠</span> WFO 深度见解
          </div>
          <p className="text-white/60 text-[10px] leading-relaxed">
            {result?.aggregatedOOSMetrics?.analysisReport || '基于蒙特卡洛与 Brinson 归因的综合分析正在生成...'}
          </p>
        </div>
      </GlassCard>
    </ScrollReveal>
  );
};

const PortfolioBacktestSection: React.FC = () => {
  const [result, setResult] = useState<PortfolioBacktestResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [lookback, setLookback] = useState(2);

  const handleRun = React.useCallback(async () => {
    setLoading(true);
    try {
      const data = await stockStrategyService.runPortfolioBacktest(1000000, lookback);
      setResult(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [lookback]);

  useEffect(() => {
    handleRun();
  }, [handleRun]);

  const chartData =
    result?.equityCurve.map((e) => ({
      time: new Date(e.time * 1000).toLocaleDateString(),
      equity: Math.round(e.equity),
      drawdown: -(e.drawdown * 100).toFixed(2),
    })) || [];

  return (
    <ScrollReveal animation="fadeInUp">
      <GlassCard className="p-4 mb-4 border-white/10">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            <AnimatedTitle variant="glow" size="sm" className="mb-0">
              组合合并回测曲线
            </AnimatedTitle>
            <p className="text-white/40 text-[10px] mt-1">模拟多策略等权配置下的整体资金走势</p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={lookback}
              onChange={(e) => setLookback(Number(e.target.value))}
              className="bg-white/5 border border-white/10 rounded-xl px-3 py-1.5 text-white text-sm focus:outline-none"
            >
              <option value={1} className="bg-slate-900">
                过去 1 年
              </option>
              <option value={2} className="bg-slate-900">
                过去 2 年
              </option>
              <option value={3} className="bg-slate-900">
                过去 3 年
              </option>
            </select>
            <button
              onClick={handleRun}
              disabled={loading}
              className="px-3 py-1.5 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/50 rounded-xl text-blue-400 text-xs transition-all flex items-center gap-1.5"
            >
              {loading ? (
                <div className="w-3 h-3 border-2 border-blue-400/30 border-t-blue-400 rounded-full animate-spin" />
              ) : (
                <span>📊</span>
              )}
              {loading ? '回测中...' : '重新运行'}
            </button>
          </div>
        </div>

        {result ? (
          <div className="space-y-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-white/5 rounded-2xl border border-white/5">
                <div className="text-white/40 text-[10px] uppercase tracking-wider mb-1">年化收益</div>
                <div className="text-xl font-bold text-orange-400">{(result.annualReturn * 100).toFixed(2)}%</div>
              </div>
              <div className="p-4 bg-white/5 rounded-2xl border border-white/5">
                <div className="text-white/40 text-[10px] uppercase tracking-wider mb-1">最大回撤</div>
                <div className="text-xl font-bold text-red-400">{(result.maxDrawdown * 100).toFixed(2)}%</div>
              </div>
              <div className="p-4 bg-white/5 rounded-2xl border border-white/5">
                <div className="text-white/40 text-[10px] uppercase tracking-wider mb-1">夏普比率</div>
                <div className="text-xl font-bold text-blue-400">{result.sharpeRatio.toFixed(2)}</div>
              </div>
              <div className="p-4 bg-white/5 rounded-2xl border border-white/5">
                <div className="text-white/40 text-[10px] uppercase tracking-wider mb-1">胜率</div>
                <div className="text-xl font-bold text-green-400">{(result.winRate * 100).toFixed(1)}%</div>
              </div>
            </div>

            <div className="h-[350px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="colorEquity" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#ffffff05" vertical={false} />
                  <XAxis
                    dataKey="time"
                    stroke="#ffffff20"
                    fontSize={10}
                    tickLine={false}
                    axisLine={false}
                    minTickGap={50}
                  />
                  <YAxis
                    stroke="#ffffff20"
                    fontSize={10}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(val) => `¥${(val / 10000).toFixed(0)}w`}
                    domain={['dataMin - 50000', 'dataMax + 50000']}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#0f172a',
                      border: '1px solid #ffffff10',
                      borderRadius: '12px',
                      fontSize: '12px',
                    }}
                    itemStyle={{ color: '#fff' }}
                  />
                  <Area
                    type="monotone"
                    dataKey="equity"
                    name="总资产"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorEquity)"
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="flex flex-wrap gap-2">
              {Object.entries(result.individualPerformance).map(([symbol, perf]: [string, any]) => (
                <div
                  key={symbol}
                  className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-[10px] text-white/60"
                >
                  <span className="text-white/80 font-medium">{perf.name}</span>:
                  <span className={perf.return >= 0 ? 'text-green-400' : 'text-red-400'}>
                    {perf.return >= 0 ? '+' : ''}
                    {Math.round(perf.return / 10000)}w
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="h-[300px] flex items-center justify-center text-white/20 italic">正在生成组合回测数据...</div>
        )}
      </GlassCard>
    </ScrollReveal>
  );
};

const StockQuantPortfolio: React.FC = () => {
  const setCurrentPage = useSetCurrentPage();
  const [data, setData] = useState<OptimizedResults | null>(null);
  const [monitors, setMonitors] = useState<StockMonitorTask[]>([]);
  const [quantPositions, setQuantPositions] = useState<StockQuantPosition[]>([]);
  const [portfolioSummary, setPortfolioSummary] = useState<QuantPortfolioSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [alertConfig, setAlertConfig] = useState<TelegramAlertConfig | null>(null);
  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
  const [isAllocationModalOpen, setIsAllocationModalOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [results, config, positions, summary] = await Promise.all([
          stockStrategyService.getOptimizedResults(),
          stockStrategyService.getTelegramAlertConfig(),
          stockService.getQuantPositions(undefined, undefined, 50),
          stockService.getQuantPortfolioSummary(),
        ]);

        setData(results);
        setAlertConfig(config);
        setQuantPositions(positions);
        setPortfolioSummary(summary);

        // 使用 API 返回的 monitors 数据
        if (results.monitors) {
          const quantMonitors = results.monitors.filter((m: any) => {
            const fixedRules = m.fixedRules;
            return fixedRules && fixedRules.type === 'quant_strategy';
          });
          setMonitors(quantMonitors);
        }
      } catch (err: any) {
        setError(err.message || '获取数据失败');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    const timer = setInterval(fetchData, 60000); // 每分钟刷新一次信号
    return () => clearInterval(timer);
  }, []);

  // 提取实时持仓
  const activePositions = quantPositions.filter((p) => p.status === 'open');
  const closedPositions = quantPositions.filter((p) => p.status === 'closed');
  const riskAnalysis = data?.riskAnalysis;
  // const industryMap = data?.industryMap || {};

  // 获取某个标的的实时监控状态
  const getMonitorStatus = (symbol: string) => {
    const monitor = monitors.find((m) => m.symbol === symbol);
    if (!monitor) return null;
    return (monitor as any).currentData;
  };

  // 提取最近信号
  const recentSignals = monitors
    .flatMap((m) => {
      const signals = m.signals || [];
      return signals.map((s) => ({ ...s, symbol: m.symbol, name: m.name }));
    })
    .filter((s) => s.triggered)
    .sort((a, b) => new Date(b.triggeredAt || 0).getTime() - new Date(a.triggeredAt || 0).getTime())
    .slice(0, 10);

  // 获取稳定性分数
  const getStabilityScore = (symbol: string) => {
    return data?.sensitivity?.[symbol]?.stabilityScore || 0;
  };

  const handleToggleMonitor = async (symbol: string) => {
    const monitor = monitors.find((m) => m.symbol === symbol);
    if (!monitor) return;

    const newStatus = monitor.status === 'running' ? 'stopped' : 'running';
    try {
      await stockStrategyService.toggleMonitorStatus(Number(monitor.monitorId), newStatus);
      // 局部更新状态，避免全量刷新
      setMonitors((prev) => prev.map((m) => (m.monitorId === monitor.monitorId ? { ...m, status: newStatus } : m)));
    } catch (err: any) {
      alert(err.message || '操作失败');
    }
  };

  const handleUpdateAlertConfig = async (config: Partial<TelegramAlertConfig>) => {
    try {
      const updated = await stockStrategyService.updateTelegramAlertConfig(config);
      setAlertConfig(updated);
    } catch (err: any) {
      throw err;
    }
  };

  const getStabilityLevel = (score: number) => {
    if (score >= 80) return { text: '极高', color: 'text-green-400' };
    if (score >= 60) return { text: '高', color: 'text-emerald-400' };
    if (score >= 40) return { text: '中', color: 'text-yellow-400' };
    return { text: '低', color: 'text-red-400' };
  };

  if (loading) {
    return (
      <GradientBackground>
        <ParticleBackground />
        <div className="min-h-screen flex items-center justify-center relative z-10">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white"></div>
        </div>
      </GradientBackground>
    );
  }

  return (
    <GradientBackground className="pb-20">
      <ParticleBackground />

      <PageHeader
        title="股票量化组合"
        onBack={() => setCurrentPage('stock-dashboard')}
        rightContent={
          <div className="flex gap-2">
            <button
              onClick={() => setIsAlertModalOpen(true)}
              className="px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-white text-xs transition-all flex items-center gap-1.5 relative"
              title="Telegram 告警设置"
            >
              <span>✈️</span> 告警设置
              {alertConfig?.isEnabled && (
                <span className="absolute top-1 right-1 w-1.5 h-1.5 bg-green-500 rounded-full border border-black" />
              )}
            </button>
            <button
              onClick={() => setIsAllocationModalOpen(true)}
              className="px-3 py-1.5 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/50 rounded-xl text-blue-400 text-xs transition-all flex items-center gap-1.5"
              title="资金分配建议"
            >
              <span>💰</span> 分配建议
            </button>
          </div>
        }
      />

      <div className="pt-16 px-4 space-y-4 relative z-10">
        {error ? (
          <GlassCard className="p-4 text-center">
            <div className="text-red-400 text-4xl mb-4">⚠️</div>
            <p className="text-white/80">{error}</p>
          </GlassCard>
        ) : (
          <>
            {/* Summary Stats */}
            <ScrollReveal animation="fadeInDown">
              <GlassCard className="p-4">
                <AnimatedTitle variant="glow" size="sm" className="mb-4">
                  组合核心指标
                </AnimatedTitle>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-white/5 rounded-xl p-3 border border-white/10">
                    <p className="text-white/50 text-[10px] mb-1">预期年化</p>
                    <p className="text-green-400 text-lg font-bold">15.44%</p>
                  </div>
                  <div className="bg-white/5 rounded-xl p-3 border border-white/10">
                    <p className="text-white/50 text-[10px] mb-1">最大回撤</p>
                    <p className="text-blue-400 text-lg font-bold">4.21%</p>
                  </div>
                  <div className="bg-white/5 rounded-xl p-3 border border-white/10">
                    <p className="text-white/50 text-[10px] mb-1">实时持仓</p>
                    <p className="text-purple-400 text-lg font-bold">{activePositions.length}</p>
                  </div>
                  <div className="bg-white/5 rounded-xl p-3 border border-white/10">
                    <p className="text-white/50 text-[10px] mb-1">运行中监控</p>
                    <p className="text-orange-400 text-lg font-bold">{monitors.length}</p>
                  </div>
                </div>
              </GlassCard>
            </ScrollReveal>

            {/* Portfolio Backtest Curve */}
            <PortfolioBacktestSection />

            <WFOAnalysisSection />

            {/* Virtual Portfolio Summary */}
            <ScrollReveal delay={20}>
              <VirtualPortfolioSummary summary={portfolioSummary} loading={loading} />
            </ScrollReveal>

            {/* Active Positions (Virtual Trading) */}
            <ScrollReveal animation="fadeInUp" delay={100}>
              <GlassCard className="p-4">
                <AnimatedTitle variant="glow" size="sm" className="mb-4">
                  实时虚拟持仓 (Virtual Trading)
                </AnimatedTitle>
                <VirtualPositionsTable positions={activePositions} />
              </GlassCard>
            </ScrollReveal>

            {/* Closed Positions (Trade History) */}
            {closedPositions.length > 0 && (
              <ScrollReveal animation="fadeInUp" delay={150}>
                <GlassCard className="p-4">
                  <AnimatedTitle variant="glow" size="sm" className="mb-4">
                    历史交易记录 (Closed Positions)
                  </AnimatedTitle>
                  <VirtualPositionsTable positions={closedPositions} />
                </GlassCard>
              </ScrollReveal>
            )}

            {/* Risk Analysis: Industry Distribution */}
            {riskAnalysis && (
              <ScrollReveal animation="fadeInUp" delay={200}>
                <GlassCard className="p-4">
                  <AnimatedTitle variant="glow" size="sm" className="mb-4">
                    组合行业风险分布
                  </AnimatedTitle>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {riskAnalysis.industryDistribution.map((item, idx) => (
                      <div key={idx} className="bg-white/5 rounded-xl p-3 border border-white/10">
                        <div className="text-white/50 text-[10px] mb-1 truncate">{item.name}</div>
                        <div className="flex items-end gap-2">
                          <span className="text-white font-bold text-sm">{item.value}只</span>
                          <span className="text-blue-400 text-[10px] font-medium">{item.percent}%</span>
                        </div>
                        <div className="w-full h-1 bg-white/5 rounded-full mt-2 overflow-hidden">
                          <div className="h-full bg-blue-500/50" style={{ width: `${item.percent}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </GlassCard>
              </ScrollReveal>
            )}

            {/* Recent Signals */}
            <ScrollReveal animation="fadeInUp" delay={250}>
              <GlassCard className="p-4">
                <AnimatedTitle variant="glow" size="sm" className="mb-4">
                  最新实时信号
                </AnimatedTitle>
                <div className="space-y-3">
                  {recentSignals.length > 0 ? (
                    recentSignals.map((sig, idx) => (
                      <div
                        key={idx}
                        className="group relative overflow-hidden bg-white/5 hover:bg-white/10 transition-all duration-300 rounded-xl p-3 border border-white/10 flex justify-between items-center"
                      >
                        {/* 背景装饰 */}
                        <div
                          className={`absolute -right-4 -top-4 w-16 h-16 blur-2xl opacity-10 group-hover:opacity-30 transition-opacity ${sig.type === 'buy' ? 'bg-green-500' : 'bg-red-500'}`}
                        />

                        <div className="flex items-center gap-3 relative z-10">
                          <div
                            className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${sig.type === 'buy' ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'}`}
                          >
                            {sig.type === 'buy' ? '买' : '卖'}
                          </div>
                          <div>
                            <div className="text-white font-bold text-sm">{sig.name || sig.symbol}</div>
                            <div className="text-white/50 text-[10px]">{sig.description}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-white/30 text-[10px]">
                            {new Date(sig.triggeredAt || '').toLocaleString()}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-white/30 text-sm italic">暂无触发信号</div>
                  )}
                </div>
              </GlassCard>
            </ScrollReveal>

            {/* Markdown Report */}
            <ScrollReveal animation="fadeInUp" delay={300}>
              <GlassCard className="p-4">
                <AnimatedTitle variant="glow" size="sm" className="mb-4">
                  验证报告
                </AnimatedTitle>
                <div className="prose prose-invert prose-sm max-w-none max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                  <ReactMarkdown>{data?.report || ''}</ReactMarkdown>
                </div>
              </GlassCard>
            </ScrollReveal>

            {/* Individual Stocks */}
            <ScrollReveal animation="fadeInUp" delay={350}>
              <GlassCard className="p-4">
                <AnimatedTitle variant="glow" size="sm" className="mb-4">
                  标的池配置 (15只)
                </AnimatedTitle>
                <div className="space-y-3">
                  {data?.configs &&
                    Object.entries(data.configs).map(([symbol, config]: [string, any]) => {
                      const score = getStabilityScore(symbol);
                      const level = getStabilityLevel(score);
                      const monitor = monitors.find((m) => m.symbol === symbol);
                      const wfoEfficiency = monitor?.tradingParams?.wfoEfficiency;

                      return (
                        <div
                          key={symbol}
                          className="group relative overflow-hidden bg-white/5 hover:bg-white/10 transition-all duration-300 rounded-xl p-4 border border-white/10 flex justify-between items-center"
                        >
                          {/* 背景装饰 */}
                          <div className="absolute -right-4 -top-4 w-16 h-16 blur-2xl opacity-10 group-hover:opacity-30 transition-opacity bg-blue-500" />

                          <div className="flex-1 relative z-10">
                            <div className="flex items-center gap-2">
                              <span className="text-white font-bold">{config.name || symbol}</span>
                              <span className="text-white/30 text-[10px]">{symbol}</span>
                              {score > 0 && (
                                <span
                                  className={`text-[9px] px-1.5 py-0.5 rounded-md bg-white/5 border border-white/10 ${level.color}`}
                                >
                                  稳健性: {level.text} ({score.toFixed(0)})
                                </span>
                              )}
                              {wfoEfficiency !== undefined && (
                                <span
                                  className={`text-[9px] px-1.5 py-0.5 rounded-md bg-blue-500/5 border border-blue-500/10 ${
                                    wfoEfficiency >= 0.7
                                      ? 'text-green-400'
                                      : wfoEfficiency >= 0.5
                                        ? 'text-blue-400'
                                        : 'text-yellow-400'
                                  }`}
                                  title="WFO 寻优效率 (IS/OOS 表现一致性)"
                                >
                                  WFO: {(wfoEfficiency * 100).toFixed(0)}%
                                </span>
                              )}
                              {monitor?.tradingParams?.sensitivityScore !== undefined && (
                                <span
                                  className={`text-[9px] px-1.5 py-0.5 rounded-md bg-blue-500/5 border border-blue-500/10 ${
                                    monitor.tradingParams.sensitivityScore >= 0.8
                                      ? 'text-green-400'
                                      : monitor.tradingParams.sensitivityScore >= 0.6
                                        ? 'text-blue-400'
                                        : 'text-yellow-400'
                                  }`}
                                  title="参数敏感度评分 (值越高表示策略越稳健，不易过度拟合)"
                                >
                                  🛡️: {(monitor.tradingParams.sensitivityScore * 100).toFixed(0)}%
                                </span>
                              )}
                              {monitor?.tradingParams?.mcStabilityScore !== undefined && (
                                <span
                                  className={`text-[9px] px-1.5 py-0.5 rounded-md bg-blue-500/5 border border-blue-500/10 ${
                                    monitor.tradingParams.mcStabilityScore >= 0.9
                                      ? 'text-green-400'
                                      : monitor.tradingParams.mcStabilityScore >= 0.7
                                        ? 'text-blue-400'
                                        : 'text-yellow-400'
                                  }`}
                                  title="蒙特卡洛稳定性评分 (基于 500 次随机路径模拟，值越高表示在各种市场条件下越稳健)"
                                >
                                  🎲: {(monitor.tradingParams.mcStabilityScore * 100).toFixed(0)}%
                                </span>
                              )}
                              {monitor?.status === 'running' ? (
                                <button
                                  onClick={() => handleToggleMonitor(symbol)}
                                  className="text-[9px] px-1.5 py-0.5 rounded-md bg-green-500/10 border border-green-500/20 text-green-400 animate-pulse hover:bg-green-500/20 transition-colors"
                                >
                                  监控中
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleToggleMonitor(symbol)}
                                  className="text-[9px] px-1.5 py-0.5 rounded-md bg-white/5 border border-white/10 text-white/30 hover:bg-white/10 transition-colors"
                                >
                                  已暂停
                                </button>
                              )}
                            </div>
                            <div className="text-white/50 text-[10px] mt-1">
                              {config.strategyCode} | 止盈 {(config.bestParams?.takeProfitPct * 100).toFixed(1)}% | 止损{' '}
                              {(config.bestParams?.stopLossPct * 100).toFixed(1)}%
                            </div>
                            {getMonitorStatus(symbol) && (
                              <div className="text-white/30 text-[9px] mt-1">
                                最新价: {getMonitorStatus(symbol).price} | 更新:{' '}
                                {new Date(getMonitorStatus(symbol).updateTime).toLocaleTimeString()}
                              </div>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="text-green-400 font-bold text-sm">+{config.expectedAnnualReturn}%</div>
                            <div className="text-white/30 text-[10px]">回撤: {config.maxDrawdown}%</div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </GlassCard>
            </ScrollReveal>
          </>
        )}
      </div>

      {alertConfig && (
        <AlertConfigModal
          isOpen={isAlertModalOpen}
          onClose={() => setIsAlertModalOpen(false)}
          config={alertConfig}
          onUpdate={handleUpdateAlertConfig}
        />
      )}
      <AllocationModal
        isOpen={isAllocationModalOpen}
        onClose={() => setIsAllocationModalOpen(false)}
        onFetch={(capital) => stockStrategyService.getPortfolioAllocation(capital)}
      />
    </GradientBackground>
  );
};

export default StockQuantPortfolio;
